import { useEffect, useState } from 'react';
import { folderSelectionModalConfig } from '../data/constants';
import TreeItem from './tree-items';
import { AwsSelected, FolderTreeProps, TreeNode } from '../kb.props';
import { getKbAwsFoldersContentById, getKbGDriveFoldersContentById } from '../services/kb';
import { Loader, MakeToast } from '../../../../components';

function FolderTree({
  data,
  className,
  folderId,
  service,
  onSelectionChange,
  selectedFolders,
  loading,
  showCheckboxes,
  disabled,
}: FolderTreeProps) {
  const [treeData, setTreeData] = useState<TreeNode[]>([]);

  const markNodeLoading = (
    nodes: TreeNode[],
    targetNodeId: string,
    isLoading: boolean,
  ): TreeNode[] => {
    return nodes.map((node) => {
      if (node.id === targetNodeId) {
        return { ...node, isLoading };
      }
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: markNodeLoading(node.children, targetNodeId, isLoading),
        };
      }
      return node;
    });
  };

  const getSelectedUploadPaths = (
    nodes: TreeNode[],
    parentPath: string = '',
  ): string[] | AwsSelected[] => {
    if (service === 'aws') {
      let selected: AwsSelected[] = [];
      nodes.forEach((node) => {
        if (node.mimeType?.includes('folder')) {
          if (node.checked && !node.indeterminate) {
            selected.push({
              bucket_name: node.bucket || '',
              prefix: node.prefix || '',
            });
          } else if (node.indeterminate || (node.children && node.children.length > 0)) {
            selected = selected.concat(
              getSelectedUploadPaths(node.children || []) as AwsSelected[],
            );
          }
        }
      });
      return selected;
    }
    let selectedPaths: string[] = [];
    nodes.forEach((node) => {
      const isFolder = node.mimeType?.includes('folder');
      const currentPath = parentPath ? `${parentPath}/${node.id}` : node.id;
      if (isFolder) {
        if (node.checked && !node.indeterminate) {
          selectedPaths.push(currentPath);
        } else if (node.indeterminate) {
          const childPaths = getSelectedUploadPaths(node.children || [], currentPath) as string[];
          selectedPaths = selectedPaths.concat(childPaths);
        } else if (node.children && node.children.length > 0) {
          const childPaths = getSelectedUploadPaths(node.children, currentPath) as string[];
          selectedPaths = selectedPaths.concat(childPaths);
        }
      }
    });
    return selectedPaths;
  };

  const bubbleUpCheckState = (nodes: TreeNode[]): TreeNode[] => {
    return nodes.map((node) => {
      if (node.children && node.children.length > 0) {
        const folderChildren = node.children.filter((child) => child.mimeType.includes('folder'));
        const children = bubbleUpCheckState(node.children);
        if (folderChildren.length === 0) {
          return {
            ...node,
            checked: !!node.checked,
            indeterminate: false,
            children,
          };
        }
        const checkedCount = children.filter(
          (c) => c.mimeType.includes('folder') && c.checked,
        ).length;
        const indeterminateCount = children.filter(
          (c) => c.mimeType.includes('folder') && c.indeterminate,
        ).length;
        let checked = false;
        let indeterminate = false;
        if (checkedCount === folderChildren.length && folderChildren.length > 0) {
          checked = true;
        } else if (checkedCount > 0 || indeterminateCount > 0) {
          indeterminate = true;
        }
        return {
          ...node,
          checked,
          indeterminate,
          children,
        };
      }
      return {
        ...node,
        checked: !!node.checked,
        indeterminate: false,
      };
    });
  };

  useEffect(() => {
    if (!data || data.length === 0) return;
    const markUploadedPaths = (nodes: TreeNode[], paths: string[]): TreeNode[] => {
      return nodes.map((node) => {
        const nodePaths = paths.filter((p) => p.startsWith(node.id));
        if (nodePaths.length === 0) return node;
        const exactMatch = nodePaths.find((p) => p === node.id);
        if (exactMatch) {
          return { ...node, checked: true, indeterminate: false };
        }
        let updatedChildren: TreeNode[] = [];
        if (node.children && node.children.length > 0) {
          updatedChildren = markUploadedPaths(
            node.children,
            nodePaths.map((p) => p.split('/').slice(1).join('/')),
          );
        }
        return {
          ...node,
          checked: false,
          indeterminate: true,
          children: updatedChildren,
        };
      });
    };
    try {
      const updatedTree = markUploadedPaths(data, selectedFolders ?? []);
      setTreeData(bubbleUpCheckState(updatedTree));
    } catch (err) {
      MakeToast('error', `Failed to initialize tree. ${err}`);
    }
  }, [data, selectedFolders]);

  const updateChildrenCheckedState = (children: TreeNode[], checked: boolean): TreeNode[] => {
    return children.map((child) => {
      const newChild = { ...child, checked };
      if (newChild.children && newChild.children.length > 0) {
        return {
          ...newChild,
          children: updateChildrenCheckedState(newChild.children, checked),
        };
      }
      return { ...newChild, checked };
    });
  };

  const updateNodeChildren = (
    nodes: TreeNode[],
    targetNodeId: string,
    newChildren: TreeNode[],
  ): TreeNode[] => {
    return nodes.map((node) => {
      if (node.id === targetNodeId) {
        const updatedChildren = newChildren.map((child) => ({
          ...child,
          parentId: targetNodeId,
          checked: node.checked,
        }));
        return { ...node, isExpanded: !node.isExpanded, children: updatedChildren };
      }
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: updateNodeChildren(node.children, targetNodeId, newChildren),
        };
      }
      return node;
    });
  };

  const findAwsTargetedNode = (nodes: TreeNode[], id: string): TreeNode | undefined => {
    let foundNode: TreeNode | undefined;
    nodes.find((node) => {
      if (node.id === id) {
        foundNode = node;
        return true;
      }
      if (node.children && node.children.length > 0) {
        const nested = findAwsTargetedNode(node.children, id);
        if (nested) {
          foundNode = nested;
          return true;
        }
      }
      return false;
    });
    return foundNode;
  };

  const fetchChildren = async (targetNodeId: string) => {
    const targetNode = findAwsTargetedNode(treeData, targetNodeId);
    if (targetNodeId) {
      try {
        let fetchedChildren: TreeNode[] = [];
        if (service === 'G-drive') {
          const response = await getKbGDriveFoldersContentById(folderId, targetNodeId);
          const folders: TreeNode[] = response?.data?.data?.folders || [];
          const files: TreeNode[] = response?.data?.data?.files || [];
          fetchedChildren = [...folders, ...files];
        } else if (service === 'aws') {
          if (!targetNode) throw new Error('Target node not found');
          const payload = {
            prefix: targetNode.prefix || '',
            bucket_name: targetNode.bucket,
          };
          const response = await getKbAwsFoldersContentById(folderId, payload);
          const folders: TreeNode[] = response?.data?.data?.folders || [];
          const files: TreeNode[] = response?.data?.data?.files || [];
          fetchedChildren = [...folders, ...files];
        }
        setTreeData((prevTreeData) => {
          let updatedTree = updateNodeChildren(prevTreeData, targetNodeId, fetchedChildren);
          setTimeout(() => {
            updatedTree = markNodeLoading(updatedTree, targetNodeId, false);
          }, 1000);
          return bubbleUpCheckState(updatedTree);
        });
      } catch (error) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          'Something went wrong while creating session!';
        MakeToast('error', msg);
      } finally {
        setTimeout(() => {
          setTreeData((prev) => markNodeLoading(prev, targetNodeId, false));
        }, 1000);
      }
    }
  };

  const handleToggleExpanded = (nodeId: string) => {
    const findNode = (nodes: TreeNode[], id: string): TreeNode | undefined => {
      let foundNode: TreeNode | undefined;
      nodes.find((node) => {
        if (node.id === id) {
          foundNode = node;
          return true;
        }
        if (node.children && node.children.length > 0) {
          const nestedFound = findNode(node.children, id);
          if (nestedFound) {
            foundNode = nestedFound;
            return true;
          }
        }
        return false;
      });
      return foundNode;
    };

    const nodeToToggle = findNode(treeData, nodeId);
    if (nodeToToggle) {
      if (nodeToToggle.isExpanded) {
        setTreeData((prev) => markNodeLoading(prev, nodeToToggle.id, false));
      } else {
        setTreeData((prev) => markNodeLoading(prev, nodeToToggle.id, true));
      }
      fetchChildren(nodeId);
    }
  };

  const handleCheckBoxChange = (check: boolean, nodeId: string) => {
    const updatedData = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            checked: check,
            children: updateChildrenCheckedState(node.children || [], check),
          };
        }
        if (node.children && node.children.length > 0) {
          return {
            ...node,
            children: updatedData(node.children),
          };
        }
        return node;
      });
    };
    const updatedTree = updatedData(treeData);
    setTreeData(bubbleUpCheckState(updatedTree));
    const selectedPaths = getSelectedUploadPaths(updatedTree);
    onSelectionChange?.(selectedPaths);
  };

  return (
    <div className={`${className}`}>
      <div className="flex items-center justify-start ">
        <h3 className="text-gray-800 text-xl font-bold">
          {folderSelectionModalConfig[0].subTitle}
        </h3>
      </div>
      <div className="py-2">
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <Loader width="10px" height="10px" />
          </div>
        ) : (
          treeData.map((node) => (
            <TreeItem
              key={node.id}
              folderId={folderId}
              node={node}
              onToggle={(n) => {
                handleToggleExpanded(n);
              }}
              level={0}
              service={service}
              onCheckboxChange={handleCheckBoxChange}
              showCheckboxes={showCheckboxes}
              disabled={disabled}
            />
          ))
        )}
      </div>
    </div>
  );
}

FolderTree.defaultProps = {
  className: '',
  onSelectionChange: () => {},
  service: 'G-drive',
};

export default FolderTree;
